import type { TeamMember } from '@taim/shared';
import { ChevronDown, Search } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useTeam } from './hooks';
import { MemberAvatar } from './MemberAvatar';

type Props = {
  /** Selected user ids. */
  value: string[];
  onChange: (ids: string[]) => void;
  /** Users to choose from; defaults to active team members from /api/users. */
  users?: TeamMember[];
  /** Trigger label (Clockify's report filter calls it "Team"). */
  label?: string;
  className?: string;
};

const matches = (m: TeamMember, q: string) =>
  !q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);

/**
 * Multi-select user picker, like Clockify's "Team" report filter: popover with search,
 * "Select all" (for the visible users) and one checkbox + avatar per user.
 */
export function UserPicker({ value, onChange, users, label = 'Team', className }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const fetched = useTeam({ active: 'true', enabled: !users });
  const all = users ?? fetched.data ?? [];
  const id = useId();

  const q = search.trim().toLowerCase();
  const visible = useMemo(() => all.filter((m) => matches(m, q)), [all, q]);
  const selected = new Set(value);
  const allVisibleSelected = visible.length > 0 && visible.every((m) => selected.has(m.id));
  const someVisibleSelected = visible.some((m) => selected.has(m.id));

  const toggle = (userId: string) =>
    onChange(selected.has(userId) ? value.filter((v) => v !== userId) : [...value, userId]);

  const toggleAll = () => {
    const visibleIds = new Set(visible.map((m) => m.id));
    onChange(
      allVisibleSelected
        ? value.filter((v) => !visibleIds.has(v))
        : [...value, ...visible.map((m) => m.id).filter((v) => !selected.has(v))],
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" className={cn('gap-1.5 uppercase tracking-wide', className)}>
          {label}
          {value.length > 0 && (
            <Badge className="h-5 min-w-5 px-1.5" aria-label={`${value.length} selected`}>
              {value.length}
            </Badge>
          )}
          <ChevronDown className="size-4 text-muted-foreground" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent role="dialog" aria-label={label} align="start" className="w-72 p-0">
        <div className="relative border-b p-2">
          <Search
            className="-translate-y-1/2 absolute top-1/2 left-4 size-4 text-muted-foreground"
            aria-hidden
          />
          <Input
            className="pl-8"
            placeholder="Search users"
            aria-label="Search users"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="max-h-72 min-h-0 overflow-y-auto py-1">
          <label
            htmlFor={`${id}-all`}
            className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted"
          >
            <Checkbox
              id={`${id}-all`}
              checked={allVisibleSelected ? true : someVisibleSelected ? 'indeterminate' : false}
              onCheckedChange={toggleAll}
              disabled={visible.length === 0}
            />
            Select all
          </label>
          {visible.map((m) => (
            <label
              key={m.id}
              htmlFor={`${id}-${m.id}`}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted"
            >
              <Checkbox
                id={`${id}-${m.id}`}
                checked={selected.has(m.id)}
                onCheckedChange={() => toggle(m.id)}
                aria-label={m.name}
              />
              <MemberAvatar name={m.name} avatarUrl={m.avatarUrl} className="size-6" />
              <span className="truncate">{m.name}</span>
            </label>
          ))}
          {visible.length === 0 && (
            <p className="px-3 py-2 text-muted-foreground text-sm">
              {fetched.isPending && !users ? 'Loading…' : 'No users found'}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

type SingleProps = {
  /** Selected user id (null: none chosen yet). */
  value: string | null;
  /** Name shown for `value` (e.g. from a report row, before the team has loaded). */
  valueName?: string;
  onChange: (id: string) => void;
  /** Trigger text when nothing is chosen. */
  placeholder?: string;
  className?: string;
};

/**
 * Single-user picker, like the user cell of Clockify's Detailed report: the trigger shows the
 * user, the popover lists active users with a search; choosing one closes it.
 */
export function SingleUserPicker({
  value,
  valueName,
  onChange,
  placeholder = 'User',
  className,
}: SingleProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const team = useTeam({ active: 'true', enabled: open });
  const q = search.trim().toLowerCase();
  const visible = (team.data ?? []).filter((m) => matches(m, q));
  const name = valueName ?? team.data?.find((m) => m.id === value)?.name;

  const choose = (id: string) => {
    setOpen(false);
    setSearch('');
    if (id !== value) onChange(id);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          title="Change user"
          className={cn('h-auto max-w-full justify-start px-2 py-1 font-normal', className)}
        >
          <span className="truncate">{name ?? placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent aria-label="Select user" align="start" className="w-64 p-0">
        <div className="border-b p-2">
          <Input
            placeholder="Search users"
            aria-label="Search users"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div role="listbox" aria-label="Users" className="max-h-72 min-h-0 overflow-y-auto py-1">
          {visible.map((m) => (
            <div
              key={m.id}
              role="option"
              aria-selected={m.id === value}
              tabIndex={-1}
              onClick={() => choose(m.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  choose(m.id);
                }
              }}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm outline-none hover:bg-accent focus:bg-accent aria-selected:bg-accent/60"
            >
              <MemberAvatar name={m.name} avatarUrl={m.avatarUrl} className="size-6" />
              <span className="truncate">{m.name}</span>
            </div>
          ))}
          {visible.length === 0 && (
            <p className="px-3 py-2 text-muted-foreground text-sm">
              {team.isPending ? 'Loading…' : 'No users found'}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

type TeammateProps = {
  /** The user whose data is shown. */
  value: string;
  /** The signed-in user: listed first, as "Me". */
  meId: string;
  onChange: (id: string) => void;
  className?: string;
};

/**
 * Clockify's "Teammates" selector (Timesheet header, admins and managers): the trigger names
 * whose timesheet is shown; the popover lists me first, then active teammates with their email,
 * with a search by name or email.
 */
export function TeammatePicker({ value, meId, onChange, className }: TeammateProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const team = useTeam({ active: 'true' });
  const all = team.data ?? [];
  const me = all.find((m) => m.id === meId);
  const others = all.filter((m) => m.id !== meId).sort((a, b) => a.name.localeCompare(b.name));
  const q = search.trim().toLowerCase();
  const visible = [
    ...(me && (!q || 'me'.includes(q) || matches(me, q)) ? [{ member: me, label: 'Me' }] : []),
    ...others.filter((m) => matches(m, q)).map((m) => ({ member: m, label: m.name })),
  ];
  const selected = all.find((m) => m.id === value);
  const label = value === meId ? 'Me' : (selected?.name ?? (team.isPending ? 'Loading…' : 'Unknown user'));

  const choose = (id: string) => {
    setOpen(false);
    setSearch('');
    if (id !== value) onChange(id);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={`Teammate: ${label}`}
          title="Teammates"
          className={cn('max-w-56 justify-start gap-2 font-normal', className)}
        >
          {selected && (
            <MemberAvatar name={selected.name} avatarUrl={selected.avatarUrl} className="size-5" />
          )}
          <span className="truncate">{label}</span>
          <ChevronDown className="ml-auto size-4 text-muted-foreground" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent aria-label="Teammates" align="end" className="w-72 p-0">
        <div className="relative border-b p-2">
          <Search
            className="-translate-y-1/2 absolute top-1/2 left-4 size-4 text-muted-foreground"
            aria-hidden
          />
          <Input
            className="pl-8"
            placeholder="Search teammates"
            aria-label="Search teammates"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div role="listbox" aria-label="Teammates" className="max-h-72 min-h-0 overflow-y-auto py-1">
          {visible.map(({ member: m, label: name }) => (
            <div
              key={m.id}
              role="option"
              aria-label={name}
              aria-selected={m.id === value}
              tabIndex={-1}
              onClick={() => choose(m.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  choose(m.id);
                }
              }}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm outline-none hover:bg-accent focus:bg-accent aria-selected:bg-accent/60"
            >
              <MemberAvatar name={m.name} avatarUrl={m.avatarUrl} className="size-6" />
              <span className="grid min-w-0">
                <span className="truncate">{name}</span>
                <span className="truncate text-muted-foreground text-xs">{m.email}</span>
              </span>
            </div>
          ))}
          {visible.length === 0 && (
            <p className="px-3 py-2 text-muted-foreground text-sm">
              {team.isPending ? 'Loading…' : 'No users found'}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
